import MediumArticle, { MediumArticleDisplayVariant } from 'components/ArticleGrid/MediumArticle';
import ArticleRowCarousel from 'components/ArticleGrid/ArticleRowCarousel';
import ArticleGridPaginated from 'components/ArticleGrid/ArticleGridPaginated';
import { RelatedArticleProps } from './types';
import { Color } from 'libs/contentful/types';
import { Colorways } from 'types/constants';

export const MediumArticleGridLayout = {
  Flex: 'flex',
  Carousel: 'carousel',
  Paginated: 'paginated',
} as const;
export type MediumArticleGridLayout = (typeof MediumArticleGridLayout)[keyof typeof MediumArticleGridLayout];

// When cycling is enabled, cards rotate through these colorways based on index.
// Only Primary and Inverse bases have defined cycles; others fall back to Primary.
const CARD_COLORWAY_CYCLES: Partial<Record<Colorways, Colorways[]>> = {
  [Colorways.Primary]: [Colorways.Secondary, Colorways.Tertiary, Colorways.Inverse],
  [Colorways.Inverse]: [Colorways.Primary, Colorways.Secondary, Colorways.Tertiary],
  [Colorways.Secondary]: [Colorways.ActionPrimary, Colorways.Tertiary, Colorways.Inverse],
};

// When cycling is disabled, every card gets the same static colorway.
// Only Primary->Secondary is mapped; all other bases fall back to Primary.
const CARD_COLORWAY_STATIC: Partial<Record<Colorways, Colorways>> = {
  [Colorways.Primary]: Colorways.Secondary,
};

interface MediumArticleGridProps {
  articles: RelatedArticleProps[];
  layout: MediumArticleGridLayout;
  displayVariant: MediumArticleDisplayVariant;
  color?: Color;
  showDates?: boolean;
  includeCategoryLinks?: boolean;
  shouldCycleColors?: boolean;
  articlesPerPage?: number;
}

// Resolves which colorway a card should use based on the parent section's base
// color, the card's position in the grid, and whether color cycling is active.
const getCardColorway = (baseColorway: Colorways, index: number, shouldCycle: boolean): Colorways => {
  // Static mode: all cards share one colorway (or fall back to Primary)
  if (!shouldCycle) return CARD_COLORWAY_STATIC[baseColorway] ?? Colorways.Primary;
  // Cycling mode: round-robin through the cycle array
  const cycle = CARD_COLORWAY_CYCLES[baseColorway];
  if (!cycle) return Colorways.Primary;
  return cycle[index % cycle.length];
};

const MediumArticleGrid = ({
  articles,
  layout,
  displayVariant,
  color = { name: Colorways.Primary, value: '' },
  showDates,
  includeCategoryLinks,
  shouldCycleColors = false,
  articlesPerPage,
}: MediumArticleGridProps) => {
  // Images throw off the forced square aspect ratio. Only apply aspect-square
  // if NONE of the articles on the grid have images.
  const noImages = articles.every(article => !article.articleImage);
  // Switch to aspect-auto at (sm < size < md) screensize where article headlines may overflow their containers.
  const noImageStyles =
    noImages && displayVariant === MediumArticleDisplayVariant.Card
      ? 'aspect-square sm:aspect-auto md:aspect-square'
      : undefined;

  const isCard = displayVariant === MediumArticleDisplayVariant.Card;

  const articleElements = articles.map((article, i) => (
    <MediumArticle
      {...article}
      displayVariant={displayVariant}
      // Only Card variants get colorways; Default variants inherit the parent's styles
      colorway={isCard ? getCardColorway(color.name, i, shouldCycleColors) : undefined}
      showDates={showDates}
      includeCategoryLinks={includeCategoryLinks}
      className={noImageStyles}
      key={article.sys?.id || i}
    />
  ));

  if (layout === MediumArticleGridLayout.Flex) {
    return <div className="flex flex-col sm:flex-row sm:items-stretch gap-4">{articleElements}</div>;
  }

  if (layout === MediumArticleGridLayout.Paginated) {
    return <ArticleGridPaginated articlesPerPage={articlesPerPage}>{articleElements}</ArticleGridPaginated>;
  }

  return (
    <ArticleRowCarousel color={color} totalItems={articles.length}>
      {articleElements}
    </ArticleRowCarousel>
  );
};

export default MediumArticleGrid;
